import { useTranslations } from 'next-intl';

import {
  AMENITIES_APARTMENT,
  AMENITIES_ROOM,
  APT_SIZE_UNITS,
  BATHROOMS,
  BEDROOMS,
  BUILDING_TYPES,
  CLEANLINESS_LEVELS,
  FOOD_PREFERENCE,
  GET_UP,
  GO_TO_BED,
  HOUSEHOLD_SEX,
  OCCUPATION,
  OVERNIGHT_GUESTS,
  PARTY_HABITS,
  PETS,
  SMOKING_HABITS,
  SMOKING_PREFERENCE,
  WORK_SCHEDULE,
} from '@/features/listing/constants/listing-details-constants';
import { type Listing } from '@/features/listing/types/listing-types';

import styles from './listing-detail-room-info.module.scss';

interface ListingDetailRoomInfoProps {
  detailsRoom: Listing['detailsRoom'],
  detailsApartment: Listing['detailsApartment'],
}

export default function ListingDetailRoomInfo(props: ListingDetailRoomInfoProps) {
  const {
    detailsApartment,
    detailsRoom,
  } = props;

  const characteristicsNamesTranslations = useTranslations('listing.characteristics.names');
  const characteristicsValuesTranslations = useTranslations('listing.characteristics.values');
  const sexTranslations = useTranslations('listing.filters.sex');

  const isHouseholdShow = detailsRoom && (detailsRoom.householdAge ?? detailsRoom.peopleInHousehold ?? detailsRoom.householdSex);
  const isResidenceRoomShow = detailsRoom && (detailsRoom.buildingType ?? detailsRoom.isFurnished ?? detailsRoom.moveInFee ?? detailsRoom.utilitiesCost ?? detailsRoom.parkingRent);
  const isResidenceApartmentShow = detailsApartment && (detailsApartment.bedroomsID ?? detailsApartment.bathroomsID ?? detailsApartment.aptSize ?? detailsApartment.aptSizeUnits);
  const isLifestyleShow = detailsRoom && (detailsRoom.cleanliness ?? detailsRoom.overnightGuests ?? detailsRoom.partyHabits ?? detailsRoom.getUp ?? detailsRoom.goToBed ?? detailsRoom.foodPreference ?? detailsRoom.mySmokingHabits ?? detailsRoom.workSchedule ?? detailsRoom.occupation ?? detailsRoom.myPets);
  const isRoommatePreferenceShow = detailsRoom && (detailsRoom.agePreference ?? detailsRoom.smokingPreference ?? detailsRoom.petsPreference ?? detailsRoom.studentsOnly);

  return (
    <div className={styles.listingDetailRoomInfo}>
      {detailsRoom?.roomAmenities && detailsRoom.roomAmenities.length > 0 && (
        <div className={styles.listingDetailRoomInfo_box}>
          <p className={styles.listingDetailRoomInfo_infoTitle}>
            {characteristicsNamesTranslations('amenities')}
          </p>

          <div className={styles.listingDetailRoomInfo_amenities}>
            {detailsRoom.roomAmenities.map((amenityId) => (
              <p key={amenityId} className={styles.listingDetailRoomInfo_amenitiesItem}>
                {characteristicsValuesTranslations(AMENITIES_ROOM[amenityId].translateToken)}
              </p>
            ))}
          </div>
        </div>
      )}

      {detailsApartment?.apartmentAmenities && detailsApartment.apartmentAmenities.length > 0 && (
        <div className={styles.listingDetailRoomInfo_box}>
          <p className={styles.listingDetailRoomInfo_infoTitle}>
            {characteristicsNamesTranslations('amenities')}
          </p>

          <div className={styles.listingDetailRoomInfo_amenities}>
            {detailsApartment.apartmentAmenities.map((amenityId) => (
              <p key={amenityId} className={styles.listingDetailRoomInfo_amenitiesItem}>
                {characteristicsValuesTranslations(AMENITIES_APARTMENT[amenityId].translateToken)}
              </p>
            ))}
          </div>
        </div>
      )}

      {isResidenceRoomShow && (
        <div className={styles.listingDetailRoomInfo_box}>
          <p className={styles.listingDetailRoomInfo_infoTitle}>
            {characteristicsNamesTranslations('residence')}
          </p>

          <div className={styles.listingDetailRoomInfo_infoList}>
            {detailsRoom.buildingType && (
            <>
              <p className={styles.listingDetailRoomInfo_infoListName}>
                {`${characteristicsNamesTranslations('building-type')}:`}
              </p>
              <p className={styles.listingDetailRoomInfo_infoListValue}>
                {characteristicsValuesTranslations(BUILDING_TYPES[detailsRoom.buildingType].translateToken)}
              </p>
            </>
            )}

            <p className={styles.listingDetailRoomInfo_infoListName}>
              {`${characteristicsNamesTranslations('furnished')}:`}
            </p>
            <p className={styles.listingDetailRoomInfo_infoListValue}>
              {detailsRoom.isFurnished ? characteristicsValuesTranslations('yes') : characteristicsValuesTranslations('no')}
            </p>

            {detailsRoom.moveInFee !== null && detailsRoom.moveInFee !== 0 && (
              <>
                <p className={styles.listingDetailRoomInfo_infoListName}>
                  {`${characteristicsNamesTranslations('move-in-fee')}:`}
                </p>
                <p className={styles.listingDetailRoomInfo_infoListValue}>
                  {detailsRoom.moveInFee}
                </p>
              </>
            )}

            {detailsRoom.utilitiesCost !== null && detailsRoom.utilitiesCost !== 0 && (
            <>
              <p className={styles.listingDetailRoomInfo_infoListName}>
                {`${characteristicsNamesTranslations('utilities-cost')}:`}
              </p>
              <p className={styles.listingDetailRoomInfo_infoListValue}>
                {detailsRoom.utilitiesCost}
              </p>
            </>
            )}

            {detailsRoom.parkingRent !== null && detailsRoom.parkingRent !== 0 && (
            <>
              <p className={styles.listingDetailRoomInfo_infoListName}>
                {`${characteristicsNamesTranslations('parking-rent')}:`}
              </p>
              <p className={styles.listingDetailRoomInfo_infoListValue}>
                {detailsRoom.parkingRent}
              </p>
            </>
            )}
          </div>
        </div>
      )}

      {isResidenceApartmentShow && (
        <div className={styles.listingDetailRoomInfo_box}>
          <p className={styles.listingDetailRoomInfo_infoTitle}>
            {characteristicsNamesTranslations('residence')}
          </p>

          <div className={styles.listingDetailRoomInfo_infoList}>
            {detailsApartment.bedroomsID && (
            <>
              <p className={styles.listingDetailRoomInfo_infoListName}>
                {`${characteristicsNamesTranslations('bedrooms')}:`}
              </p>
              <p className={styles.listingDetailRoomInfo_infoListValue}>
                {characteristicsValuesTranslations(BEDROOMS[detailsApartment.bedroomsID].translateToken)}
              </p>
            </>
            )}

            {detailsApartment.bathroomsID && (
            <>
              <p className={styles.listingDetailRoomInfo_infoListName}>
                {`${characteristicsNamesTranslations('bathrooms')}:`}
              </p>
              <p className={styles.listingDetailRoomInfo_infoListValue}>
                {characteristicsValuesTranslations(BATHROOMS[detailsApartment.bathroomsID].translateToken)}
              </p>
            </>
            )}

            <p className={styles.listingDetailRoomInfo_infoListName}>
              {`${characteristicsNamesTranslations('furnished')}:`}
            </p>
            <p
              className={styles.listingDetailRoomInfo_infoListValue}
            >
              {detailsApartment.isFurnished ? characteristicsValuesTranslations('yes') : characteristicsValuesTranslations('no')}
            </p>

            {detailsApartment.aptSize && detailsApartment.aptSizeUnits !== null && (
            <>
              <p className={styles.listingDetailRoomInfo_infoListName}>
                {`${characteristicsNamesTranslations('apartment-size')}:`}
              </p>
              <p className={styles.listingDetailRoomInfo_infoListValue}>
                {detailsApartment.aptSize}
                &nbsp;
                {characteristicsValuesTranslations(APT_SIZE_UNITS[detailsApartment.aptSizeUnits].translateToken)}
              </p>
            </>
            )}
          </div>
        </div>
      )}

      {isHouseholdShow && (
        <div className={styles.listingDetailRoomInfo_box}>
          <p className={styles.listingDetailRoomInfo_infoTitle}>
            {characteristicsNamesTranslations('household')}
          </p>

          <div className={styles.listingDetailRoomInfo_infoList}>
            {detailsRoom.householdAge && (
              <>
                <p className={styles.listingDetailRoomInfo_infoListName}>
                  {`${characteristicsNamesTranslations('household-age')}:`}
                </p>
                <p className={styles.listingDetailRoomInfo_infoListValue}>
                  {detailsRoom.householdAge}
                </p>
              </>
            )}

            {detailsRoom.peopleInHousehold !== null && (
              <>
                <p className={styles.listingDetailRoomInfo_infoListName}>
                  {`${characteristicsNamesTranslations('people-in-household')}:`}
                </p>
                <p className={styles.listingDetailRoomInfo_infoListValue}>
                  {detailsRoom.peopleInHousehold}
                </p>
              </>
            )}

            {detailsRoom.householdSex && (
              <>
                <p className={styles.listingDetailRoomInfo_infoListName}>
                  {`${characteristicsNamesTranslations('household-sex')}:`}
                </p>
                <p className={styles.listingDetailRoomInfo_infoListValue}>
                  {sexTranslations(HOUSEHOLD_SEX[detailsRoom.householdSex].translateToken)}
                </p>
              </>
            )}
          </div>
        </div>
      )}

      {isLifestyleShow && (
        <div className={styles.listingDetailRoomInfo_box}>
          <p className={styles.listingDetailRoomInfo_infoTitle}>
            {characteristicsNamesTranslations('lifestyle')}
          </p>

          <div className={styles.listingDetailRoomInfo_infoList}>
            {detailsRoom.cleanliness && (
              <>
                <p className={styles.listingDetailRoomInfo_infoListName}>
                  {`${characteristicsNamesTranslations('my-cleanliness')}:`}
                </p>
                <p className={styles.listingDetailRoomInfo_infoListValue}>
                  {characteristicsValuesTranslations(CLEANLINESS_LEVELS[detailsRoom.cleanliness].translateToken)}
                </p>
              </>
            )}

            {detailsRoom.overnightGuests && (
              <>
                <p className={styles.listingDetailRoomInfo_infoListName}>
                  {`${characteristicsNamesTranslations('overnight-guests')}:`}
                </p>
                <p className={styles.listingDetailRoomInfo_infoListValue}>
                  {characteristicsValuesTranslations(OVERNIGHT_GUESTS[detailsRoom.overnightGuests].translateToken)}
                </p>
              </>
            )}

            {detailsRoom.partyHabits && (
              <>
                <p className={styles.listingDetailRoomInfo_infoListName}>
                  {`${characteristicsNamesTranslations('party-habits')}:`}
                </p>
                <p className={styles.listingDetailRoomInfo_infoListValue}>
                  {characteristicsValuesTranslations(PARTY_HABITS[detailsRoom.partyHabits].translateToken)}
                </p>
              </>
            )}

            {(detailsRoom.getUp ?? detailsRoom.goToBed) && (
            <>
              <p className={styles.listingDetailRoomInfo_infoListName}>
                {`${characteristicsNamesTranslations('i-get-up')}/${characteristicsNamesTranslations('go-to-bed')}:`}
              </p>
              <p className={styles.listingDetailRoomInfo_infoListValue}>
                {detailsRoom.getUp ? characteristicsValuesTranslations(GET_UP[detailsRoom.getUp].translateToken) : '-'}
                <span>&nbsp;/&nbsp;</span>
                {detailsRoom.goToBed ? characteristicsValuesTranslations(GO_TO_BED[detailsRoom.goToBed].translateToken) : '-'}
              </p>
            </>
            )}

            {detailsRoom.foodPreference && (
              <>
                <p className={styles.listingDetailRoomInfo_infoListName}>
                  {`${characteristicsNamesTranslations('food-preference')}:`}
                </p>
                <p className={styles.listingDetailRoomInfo_infoListValue}>
                  {characteristicsValuesTranslations(FOOD_PREFERENCE[detailsRoom.foodPreference].translateToken)}
                </p>
              </>
            )}

            {detailsRoom.mySmokingHabits && (
            <>
              <p className={styles.listingDetailRoomInfo_infoListName}>
                {`${characteristicsNamesTranslations('smoker')}:`}
              </p>
              <p className={styles.listingDetailRoomInfo_infoListValue}>
                {characteristicsValuesTranslations(SMOKING_HABITS[detailsRoom.mySmokingHabits].translateToken)}
              </p>
            </>
            )}

            {detailsRoom.workSchedule && (
            <>
              <p className={styles.listingDetailRoomInfo_infoListName}>
                {`${characteristicsNamesTranslations('work-schedule')}:`}
              </p>
              <p className={styles.listingDetailRoomInfo_infoListValue}>
                {characteristicsValuesTranslations(WORK_SCHEDULE[detailsRoom.workSchedule].translateToken)}
              </p>
            </>
            )}

            {detailsRoom.occupation && (
            <>
              <p className={styles.listingDetailRoomInfo_infoListName}>
                {`${characteristicsNamesTranslations('occupation')}:`}
              </p>
              <p className={styles.listingDetailRoomInfo_infoListValue}>
                {characteristicsValuesTranslations(OCCUPATION[detailsRoom.occupation].translateToken)}
              </p>
            </>
            )}

            {detailsRoom.myPets && detailsRoom.myPets.length > 0 && (
            <>
              <p className={styles.listingDetailRoomInfo_infoListName}>
                {`${characteristicsNamesTranslations('pets-owner')}:`}
              </p>
              <p className={styles.listingDetailRoomInfo_infoListValue}>
                {detailsRoom.myPets.map((petId, index) => {
                  if (detailsRoom.myPets?.length === 1) {
                    return characteristicsValuesTranslations(PETS[petId].translateToken);
                  }

                  if (index === Number(detailsRoom.myPets?.length) - 1) {
                    return characteristicsValuesTranslations(PETS[petId].translateToken);
                  }

                  return `${characteristicsValuesTranslations(PETS[petId].translateToken)}, `;
                })}
              </p>
            </>
            )}
          </div>
        </div>
      )}

      {isRoommatePreferenceShow && (
        <div className={styles.listingDetailRoomInfo_box}>
          <p className={styles.listingDetailRoomInfo_infoTitle}>
            {characteristicsNamesTranslations('roommate-preference')}
          </p>

          <div className={styles.listingDetailRoomInfo_infoList}>
            {detailsRoom.agePreference && (
              <>
                <p className={styles.listingDetailRoomInfo_infoListName}>
                  {`${characteristicsNamesTranslations('age')}:`}
                </p>
                <p className={styles.listingDetailRoomInfo_infoListValue}>
                  {detailsRoom.agePreference}
                </p>
              </>
            )}

            {detailsRoom.smokingPreference && (
              <>
                <p className={styles.listingDetailRoomInfo_infoListName}>
                  {`${characteristicsNamesTranslations('smoking')}:`}
                </p>
                <p className={styles.listingDetailRoomInfo_infoListValue}>
                  {characteristicsValuesTranslations(SMOKING_PREFERENCE[detailsRoom.smokingPreference].translateToken)}
                </p>
              </>
            )}

            {detailsRoom.studentsOnly && (
              <>
                <p className={styles.listingDetailRoomInfo_infoListName}>
                  {`${characteristicsNamesTranslations('students-only')}:`}
                </p>
                <p className={styles.listingDetailRoomInfo_infoListValue}>
                  {characteristicsValuesTranslations('yes')}
                </p>
              </>
            )}

            {detailsRoom.petsPreference && detailsRoom.petsPreference.length > 0 && (
              <>
                <p className={styles.listingDetailRoomInfo_infoListName}>
                  {`${characteristicsNamesTranslations('pets')}:`}
                </p>
                <p className={styles.listingDetailRoomInfo_infoListValue}>
                  {detailsRoom.petsPreference.map((petId, index) => {
                    if (detailsRoom.petsPreference?.length === 1) {
                      return characteristicsValuesTranslations(PETS[petId].translateToken);
                    }

                    if (index === Number(detailsRoom.petsPreference?.length) - 1) {
                      return characteristicsValuesTranslations(PETS[petId].translateToken);
                    }

                    return `${characteristicsValuesTranslations(PETS[petId].translateToken)}, `;
                  })}
                </p>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
